import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { first as _first } from 'lodash';
import type { User, OPTIMIZATION_TYPE, WayPoint } from '@bringg/types';

import {
	EtaFetchFunc,
	RunChangedParams,
	RouteGroup,
	RoutesPlannerTask,
	EtaCalculationUpdateResults,
	DriverRoute,
	FullEtaRequest,
	EtaResponseType,
	EtaResponseTaskItem,
	EtaResponse,
	EtaCalculationResultsData,
	EtaResponseBreakItem,
	EtaResponseDirectionsItem,
	RoutesPlannerBreak,
	CalculatePlannedEta
} from './types';

export class EtaCalculator {
	constructor(
		public fetchEta: EtaFetchFunc,
		public calculatePlannedEta: CalculatePlannedEta,
		private currentUser: User
	) {}

	public async runEtaCalculation(
		changedRoutesData: RunChangedParams[],
		setInProgressEtaCalculations: (runId: number, requestUuid?: string) => void,
		optimizationType: OPTIMIZATION_TYPE,
		teamId: number
	) {
		const requests = changedRoutesData
			.filter(routeData => routeData.runOrderedTasks.length > 0)
			.map(async routeData => {
				return this.executeEtaCalculation(
					[routeData],
					setInProgressEtaCalculations,
					optimizationType,
					teamId,
					this.fetchEta
				);
			});

		return Promise.all(requests);
	}

	public async runEtaCalculations(
		routesGroupedByResources: RouteGroup[],
		setInProgressEtaCalculations: (runId: number, requestUuid?: string) => void,
		optimizationType: OPTIMIZATION_TYPE,
		teamId: number,
		allowPastCalculation?: boolean
	): Promise<EtaCalculationUpdateResults[]> {
		const results = await Promise.all(
			routesGroupedByResources.map(async group => {
				return this.executeEtaCalculation(
					group.routes,
					setInProgressEtaCalculations,
					optimizationType,
					teamId,
					this.fetchEta,
					group.totalRunIds,
					group.minStartTimeMs,
					allowPastCalculation
				);
			})
		);
		return results.reduce((acc, val) => acc.concat(val), []);
	}

	public async runCalculatePlannedEta(
		routesGroupedByResources: RouteGroup[],
		setInProgressEtaCalculations: (runId: number, requestUuid?: string) => void,
		optimizationType: OPTIMIZATION_TYPE,
		teamId: number,
		allowPastCalculation?: boolean
	) {
		const results = await Promise.all(
			routesGroupedByResources.map(async group => {
				const requestUuid = uuid();
				const startPoint = this.getStartPointForEtaRecalculation(group.routes[0].runOrderedTasks, 1).start;

				const changedRoutesSortByStartTime = group.routes
					.slice()
					.sort((routeA, routeB) => routeA.runStartTime - routeB.runStartTime);

				const driverRoutes = changedRoutesSortByStartTime.map(this.buildEtaRequest.bind(this));

				try {
					const driverRoutesEtaRequest = this.buildFullEtaRequest(
						driverRoutes,
						startPoint,
						requestUuid,
						optimizationType,
						teamId,
						group.totalRunIds,
						group.minStartTimeMs,
						allowPastCalculation
					);
					return await this.calculatePlannedEta(driverRoutesEtaRequest);
				} catch (e) {
					console.error('failed to calculate eta request ', e);
				}
			})
		);
		return results.reduce((acc, val) => acc.concat(val), []);
	}

	private async executeEtaCalculation(
		changedRoutesData: RunChangedParams[],
		setInProgressEtaCalculations: (runId: number, requestUuid?: string) => void,
		optimizationType: OPTIMIZATION_TYPE,
		teamId: number,
		etaFunction: EtaFetchFunc | CalculatePlannedEta,
		totalRunIds?: number[],
		minStartTimeMs?: number,
		allowPastCalculation?: boolean
	): Promise<EtaCalculationUpdateResults> {
		const requestUuid = uuid();
		const startPoint = this.getStartPointForEtaRecalculation(changedRoutesData[0].runOrderedTasks, 1).start;

		const changedRoutesSortByStartTime = changedRoutesData
			.slice()
			.sort((routeA, routeB) => routeA.runStartTime - routeB.runStartTime);

		const driverRoutes = changedRoutesSortByStartTime.map(this.buildEtaRequest.bind(this));

		for (const driverRoute of driverRoutes) {
			setInProgressEtaCalculations(driverRoute.run_id, requestUuid);
		}

		try {
			const driverRoutesEtaRequest = this.buildFullEtaRequest(
				driverRoutes,
				startPoint,
				requestUuid,
				optimizationType,
				teamId,
				totalRunIds,
				minStartTimeMs,
				allowPastCalculation
			);
			const etaResponse = await etaFunction(driverRoutesEtaRequest);
			return this.getRoutesChangesWithEtaResponse(etaResponse);
		} finally {
			for (const driverRoute of driverRoutes) {
				setInProgressEtaCalculations(driverRoute.run_id);
			}
		}
	}

	public buildEtaRequest = (route: RunChangedParams): DriverRoute => {
		const { runOrderedTasks, vehicleId, routeIndex, trailerId } = route;
		const userId = route.driverId;

		return {
			driver_id: userId,
			vehicle_id: vehicleId,
			trailer_id: trailerId,
			run_id: _first(runOrderedTasks)?.run_id,
			tasks: runOrderedTasks.map(task => ({
				task_id: task.id,
				way_point_id: task.way_points[0].id
			})),
			route_idx: routeIndex
		};
	};

	public buildFullEtaRequest = (
		driverRoutes: DriverRoute[],
		start: string,
		request_uuid: string,
		optimizationType: OPTIMIZATION_TYPE,
		teamId: number,
		totalRunIds?: number[],
		minStartTimeMs?: number,
		allowPastCalculation?: boolean
	): FullEtaRequest => {
		const scheduledDate = moment(start).startOf('day').format('YYYY-MM-DD');

		const tasksIds = driverRoutes
			.map(_ => _.tasks.map(task => task.task_id))
			.reduce((acc, val) => acc.concat(val), []);

		return {
			team_id: teamId,
			driver_ids: [...new Set(driverRoutes.filter(r => r.driver_id).map(r => r.driver_id))],
			task_ids: [...new Set(tasksIds)],
			optimization_type: optimizationType,
			routes: driverRoutes,
			scheduled_date: scheduledDate, // get from merge request
			preserve_current_runs: false, // get from merge request
			merchant_id: this.currentUser.merchant_id,
			user_id: this.currentUser.id,
			request_uuid,
			optimize_for: 'custom',
			window_start: moment(scheduledDate).clone().add(0, 'hours').toJSON(), // TODO: start & end of day by merchant timezone
			window_end: moment(scheduledDate).clone().add(23, 'hours').toJSON(),
			total_run_ids: totalRunIds,
			min_start_time_ms: minStartTimeMs,
			allow_past_calculation: !!allowPastCalculation
		};
	};

	public getRoutesChangesWithEtaResponse = (etaRecalculateResult: EtaResponse): EtaCalculationResultsData => {
		const routesChanges = {
			updatedRunsTasks: [],
			updatedRunsBreaks: [],
			updatedRunsDirections: [],
			updatedRoutesInfo: []
		};

		return etaRecalculateResult.reduce((allRoutesChanges, result) => {
			if (result.type === EtaResponseType.Task) {
				allRoutesChanges.updatedRunsTasks.push(result as EtaResponseTaskItem);
			} else if (
				result.type === EtaResponseType.ActualBreak ||
				result.type === EtaResponseType.PreassignedBreak
			) {
				allRoutesChanges.updatedRunsBreaks.push(result as EtaResponseBreakItem);
			} else if (result.type === EtaResponseType.Directions) {
				allRoutesChanges.updatedRunsDirections.push(result as EtaResponseDirectionsItem);
			} else if (result.type === EtaResponseType.RouteInfo) {
				allRoutesChanges.updatedRoutesInfo.push(result as EtaResponseDirectionsItem);
			}

			return allRoutesChanges;
		}, routesChanges);
	};

	private getWayPointStartTime = (wayPoint: WayPoint, fields: string[]) => {
		const nonEmptyField = fields.find(field => wayPoint[field]);
		return nonEmptyField ? wayPoint[nonEmptyField] : undefined;
	};

	public getStartPointForEtaRecalculation = (
		orderedTasks: RoutesPlannerTask[],
		changedPointInTheRoute: number,
		actualBreaks?: RoutesPlannerBreak[]
	): { start: string } => {
		// when break exists on the reference point:
		if (actualBreaks?.length && actualBreaks[0].position === changedPointInTheRoute) {
			return {
				start: actualBreaks[0].estimated_end_time
			};
		}

		const wayPointFieldForStartTime = ['eta'];
		if (this.currentUser.feature_flags?.prefer_scheduled_at_over_no_earlier_than_for_eta_calculation) {
			wayPointFieldForStartTime.push('scheduled_at');
			wayPointFieldForStartTime.push('no_earlier_than');
		} else {
			wayPointFieldForStartTime.push('no_earlier_than');
			wayPointFieldForStartTime.push('scheduled_at');
		}

		let taskAtReferencePoint = orderedTasks.find(task => task.priority === changedPointInTheRoute);
		if (!taskAtReferencePoint) {
			taskAtReferencePoint = _first(orderedTasks); // TODO: depot lat/lng?
		}

		return {
			start: this.getWayPointStartTime(taskAtReferencePoint.way_points[0], wayPointFieldForStartTime)
		};
	};
}

export default EtaCalculator;
